import { Slot } from '@radix-ui/react-slot';
import { Button } from '@seller-hermes/features/ui-v2/Button';
import Input from '@seller-hermes/features/ui-v2/Input';
import { Separator } from '@seller-hermes/features/ui-v2/Separator';
import Sheet from '@seller-hermes/features/ui-v2/Sheet';
import Skeleton from '@seller-hermes/features/ui-v2/Skeleton';
import Tooltip from '@seller-hermes/features/ui-v2/Tooltip';
import { useIsMobile } from '@seller-hermes/hooks/use-mobile';
import {
  mergeButtonRefs,
  useSidebarResize,
} from '@seller-hermes/hooks/use-sidebar-resize';
import { cn } from '@seller-hermes/utils/cn';
import { VariantProps, cva } from 'class-variance-authority';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import * as React from 'react';

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '3rem';

const MIN_SIDEBAR_WIDTH = '16rem';
const MAX_SIDEBAR_WIDTH = '50rem';

type DoubleSidebarState = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  toggleSidebar: () => void;
  width: string;
  setWidth: (width: string) => void;
  isDraggingRail: boolean;
  setIsDraggingRail: (isDraggingRail: boolean) => void;
};

type DoubleSidebarContextProps = {
  left: DoubleSidebarState;
  right: DoubleSidebarState;
  isMobile: boolean;
};

const DoubleSidebarContext =
  React.createContext<DoubleSidebarContextProps | null>(null);
const DoubleSidebarInnerContext = React.createContext<'left' | 'right' | null>(
  null,
);

function useDoubleSidebar() {
  const context = React.useContext(DoubleSidebarContext);
  const side = React.useContext(DoubleSidebarInnerContext);

  if (!context) {
    throw new Error(
      'useDoubleSidebar must be used within a DoubleSidebarProvider.',
    );
  }

  const currentSide = side || 'left';

  return {
    ...context[currentSide],
    isMobile: context.isMobile,
    side: currentSide,
  };
}

function useDoubleSidebarWithSide(side: 'left' | 'right') {
  const context = React.useContext(DoubleSidebarContext);

  if (!context) {
    throw new Error(
      'useDoubleSidebarWithSide must be used within a DoubleSidebarProvider.',
    );
  }

  return {
    ...context[side],
    isMobile: context.isMobile,
    side,
  };
}

const DoubleSidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    defaultOpenLeft?: boolean;
    defaultOpenRight?: boolean;
    openLeft?: boolean;
    openRight?: boolean;
    onOpenChangeLeft?: (open: boolean) => void;
    onOpenChangeRight?: (open: boolean) => void;
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    defaultWidth?: string;
    defaultWidthLeft?: string;
    defaultWidthRight?: string;
  }
>(
  (
    {
      defaultOpenLeft = true,
      defaultOpenRight = true,
      openLeft: openLeftProp,
      openRight: openRightProp,
      onOpenChangeLeft: setOpenLeftProp,
      onOpenChangeRight: setOpenRightProp,
      defaultOpen,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      defaultWidth = SIDEBAR_WIDTH,
      defaultWidthLeft,
      defaultWidthRight,
      ...props
    },
    ref,
  ) => {
    const isMobile = useIsMobile();

    const [widthLeft, setWidthLeft] = React.useState(
      defaultWidthLeft || defaultWidth,
    );
    const [widthRight, setWidthRight] = React.useState(
      defaultWidthRight || defaultWidth,
    );

    const [isDraggingRailLeft, setIsDraggingRailLeft] = React.useState(false);
    const [isDraggingRailRight, setIsDraggingRailRight] = React.useState(false);

    const [openMobileLeft, setOpenMobileLeft] = React.useState(false);
    const [_openLeft, _setOpenLeft] = React.useState(
      defaultOpen ?? defaultOpenLeft,
    );
    const openLeft = openProp ?? openLeftProp ?? _openLeft;

    const [openMobileRight, setOpenMobileRight] = React.useState(false);
    const [_openRight, _setOpenRight] = React.useState(defaultOpenRight);
    const openRight = openRightProp ?? _openRight;

    const setOpenLeft = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === 'function' ? value(openLeft) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else if (setOpenLeftProp) {
          setOpenLeftProp(openState);
        } else {
          _setOpenLeft(openState);
        }
        document.cookie = `${SIDEBAR_COOKIE_NAME}_left=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      },
      [setOpenProp, setOpenLeftProp, openLeft],
    );

    const setOpenRight = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState =
          typeof value === 'function' ? value(openRight) : value;
        if (setOpenRightProp) {
          setOpenRightProp(openState);
        } else {
          _setOpenRight(openState);
        }
        document.cookie = `${SIDEBAR_COOKIE_NAME}_right=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      },
      [setOpenRightProp, openRight],
    );

    const toggleSidebarLeft = React.useCallback(() => {
      return isMobile
        ? setOpenMobileLeft((open) => !open)
        : setOpenLeft((open) => !open);
    }, [isMobile, setOpenLeft]);

    const toggleSidebarRight = React.useCallback(() => {
      return isMobile
        ? setOpenMobileRight((open) => !open)
        : setOpenRight((open) => !open);
    }, [isMobile, setOpenRight]);

    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        // Ctrl/Cmd+B é reservado pro seletor global de filial (app-sidebar).
        // Aqui mantemos só o Ctrl/Cmd+L pro toggle do sidebar direito.
        if (event.metaKey || event.ctrlKey) {
          if (event.key === 'l') {
            event.preventDefault();
            toggleSidebarRight();
          }
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toggleSidebarRight]);

    const leftState: DoubleSidebarState = {
      state: openLeft ? 'expanded' : 'collapsed',
      open: openLeft,
      setOpen: setOpenLeft,
      openMobile: openMobileLeft,
      setOpenMobile: setOpenMobileLeft,
      toggleSidebar: toggleSidebarLeft,
      width: widthLeft,
      setWidth: setWidthLeft,
      isDraggingRail: isDraggingRailLeft,
      setIsDraggingRail: setIsDraggingRailLeft,
    };

    const rightState: DoubleSidebarState = {
      state: openRight ? 'expanded' : 'collapsed',
      open: openRight,
      setOpen: setOpenRight,
      openMobile: openMobileRight,
      setOpenMobile: setOpenMobileRight,
      toggleSidebar: toggleSidebarRight,
      width: widthRight,
      setWidth: setWidthRight,
      isDraggingRail: isDraggingRailRight,
      setIsDraggingRail: setIsDraggingRailRight,
    };

    const contextValue = React.useMemo<DoubleSidebarContextProps>(
      () => ({
        left: leftState,
        right: rightState,
        isMobile,
      }),
      [
        leftState.state,
        leftState.open,
        leftState.openMobile,
        leftState.width,
        leftState.isDraggingRail,
        rightState.state,
        rightState.open,
        rightState.openMobile,
        rightState.width,
        rightState.isDraggingRail,
        isMobile,
        setOpenLeft,
        setOpenRight,
        setOpenMobileLeft,
        setOpenMobileRight,
        toggleSidebarLeft,
        toggleSidebarRight,
        setWidthLeft,
        setWidthRight,
        setIsDraggingRailLeft,
        setIsDraggingRailRight,
      ],
    );

    return (
      <DoubleSidebarContext.Provider value={contextValue}>
        <Tooltip.Provider delayDuration={0}>
          <div
            data-slot="sidebar-wrapper"
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH,
                '--sidebar-width-left': widthLeft,
                '--sidebar-width-right': widthRight,
                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                ...style,
              } as React.CSSProperties
            }
            className={cn(
              'group/sidebar-wrapper min-h-svh has-data-[variant=inset]:bg-sidebar flex w-full',
              className,
            )}
            ref={ref}
            {...props}
          >
            {children}
          </div>
        </Tooltip.Provider>
      </DoubleSidebarContext.Provider>
    );
  },
);
DoubleSidebarProvider.displayName = 'DoubleSidebarProvider';

const DoubleSidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    side?: 'left' | 'right';
    variant?: 'sidebar' | 'floating' | 'inset';
    collapsible?: 'offcanvas' | 'icon' | 'none';
  }
>(
  (
    {
      side = 'left',
      variant = 'sidebar',
      collapsible = 'offcanvas',
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const context = React.useContext(DoubleSidebarContext);

    if (!context) {
      throw new Error('Sidebar must be used within a SidebarProvider.');
    }

    const { state, openMobile, setOpenMobile, width, isDraggingRail } =
      context[side];
    const { isMobile } = context;

    if (collapsible === 'none') {
      return (
        <DoubleSidebarInnerContext.Provider value={side}>
          <div
            ref={ref}
            data-slot="sidebar"
            data-side={side}
            className={cn(
              'bg-sidebar text-sidebar-foreground flex h-full w-[var(--sidebar-width)] flex-col',
              className,
            )}
            style={
              {
                '--sidebar-width': width,
              } as React.CSSProperties
            }
            {...props}
          >
            {children}
          </div>
        </DoubleSidebarInnerContext.Provider>
      );
    }

    if (isMobile) {
      return (
        <DoubleSidebarInnerContext.Provider value={side}>
          <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
            <Sheet.Content
              data-sidebar="sidebar"
              data-slot="sidebar"
              data-mobile="true"
              className="bg-sidebar text-sidebar-foreground w-[var(--sidebar-width)] p-0 [&>button]:hidden"
              style={
                {
                  '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
                } as React.CSSProperties
              }
              side={side}
            >
              <Sheet.Header className="sr-only">
                <Sheet.Title>Sidebar</Sheet.Title>
                <Sheet.Description>
                  Displays the mobile sidebar.
                </Sheet.Description>
              </Sheet.Header>
              <div className="flex h-full w-full flex-col">{children}</div>
            </Sheet.Content>
          </Sheet>
        </DoubleSidebarInnerContext.Provider>
      );
    }

    return (
      <DoubleSidebarInnerContext.Provider value={side}>
        <div
          ref={ref}
          className="text-sidebar-foreground group peer hidden md:block"
          data-state={state}
          data-collapsible={state === 'collapsed' ? collapsible : ''}
          data-variant={variant}
          data-side={side}
          data-slot="sidebar"
          style={
            {
              '--sidebar-width': width,
            } as React.CSSProperties
          }
        >
          <div
            data-slot="sidebar-gap"
            className={cn(
              'relative w-[var(--sidebar-width)] bg-transparent',
              {
                'transition-[width] duration-200 ease-linear': !isDraggingRail,
              },
              'group-data-[collapsible=offcanvas]:w-0',
              'group-data-[side=right]:rotate-180',
              variant === 'floating' || variant === 'inset'
                ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]'
                : 'group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]',
            )}
          />
          <div
            data-slot="sidebar-container"
            className={cn(
              'h-svh fixed inset-y-0 z-10 hidden w-[var(--sidebar-width)] md:flex',
              {
                'transition-[left,right,width] duration-200 ease-linear':
                  !isDraggingRail,
              },
              side === 'left'
                ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
                : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
              // Adjust the padding for floating and inset variants.
              variant === 'floating' || variant === 'inset'
                ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]'
                : 'group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)] group-data-[side=left]:border-r group-data-[side=right]:border-l',
              className,
            )}
            {...props}
          >
            <div
              data-sidebar="sidebar"
              data-slot="sidebar-inner"
              className="bg-sidebar group-data-[variant=floating]:border-sidebar-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow"
            >
              {children}
            </div>
          </div>
        </div>
      </DoubleSidebarInnerContext.Provider>
    );
  },
);
DoubleSidebar.displayName = 'DoubleSidebar';

const DoubleSidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button> & {
    side?: 'left' | 'right';
    asChild?: boolean;
  }
>(({ className, onClick, side, asChild = false, children, ...props }, ref) => {
  const contextSide = React.useContext(DoubleSidebarInnerContext);
  const targetSide = side || contextSide || 'left';
  const { toggleSidebar, open } = useDoubleSidebarWithSide(targetSide);

  const Comp = asChild ? Slot : Button;

  return (
    <Comp
      ref={ref}
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      data-side={targetSide}
      data-state={open ? 'open' : 'closed'}
      variant="ghost"
      size="icon"
      className={cn(
        'data-[state=open]:bg-muted-foreground data-[state=open]:text-background h-6 w-6 rounded-md [&>svg]:h-5 [&>svg]:w-5',
        className,
      )}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      {children ? (
        children
      ) : (
        <Tooltip>
          <Tooltip.Provider delayDuration={0}>
            <Tooltip.Trigger asChild>
              {open ? (
                <PanelLeftClose
                  className={cn({
                    'rotate-180': targetSide === 'right',
                  })}
                />
              ) : (
                <PanelLeftOpen />
              )}
            </Tooltip.Trigger>
            <Tooltip.Content side="bottom" align={open ? 'center' : 'start'}>
              {open ? 'Ocultar menu de navegação' : 'Mostrar menu de navegação'}
            </Tooltip.Content>
          </Tooltip.Provider>
        </Tooltip>
      )}
      <span className="sr-only">Toggle Sidebar</span>
    </Comp>
  );
});
DoubleSidebarTrigger.displayName = 'DoubleSidebarTrigger';

interface DoubleSidebarRailProps extends React.ComponentProps<'button'> {
  enableDrag?: boolean;
  minSidebarWidth?: string;
  maxSidebarWidth?: string;
}

const DoubleSidebarRail = React.forwardRef<
  HTMLButtonElement,
  DoubleSidebarRailProps
>(
  (
    {
      className,
      enableDrag = true,
      minSidebarWidth = MIN_SIDEBAR_WIDTH,
      maxSidebarWidth = MAX_SIDEBAR_WIDTH,
      ...props
    },
    ref,
  ) => {
    const { toggleSidebar, setWidth, state, width, setIsDraggingRail, side } =
      useDoubleSidebar();

    const { dragRef, handleMouseDown } = useSidebarResize({
      direction: side === 'right' ? 'left' : 'right',
      enableDrag,
      onResize: setWidth!,
      onToggle: toggleSidebar,
      currentWidth: width!,
      isCollapsed: state === 'collapsed',
      minResizeWidth: minSidebarWidth,
      maxResizeWidth: maxSidebarWidth,
      setIsDraggingRail,
      widthCookieName: `sidebar:width:${side}`,
      widthCookieMaxAge: 60 * 60 * 24 * 7,
    });

    const combinedRef = React.useMemo(() => {
      if (!ref) {
        return dragRef;
      }

      return mergeButtonRefs([ref, dragRef]);
    }, [ref, dragRef]);

    return (
      <button
        ref={combinedRef}
        data-sidebar="rail"
        aria-label="Toggle Sidebar"
        onMouseDown={handleMouseDown}
        title="Toggle Sidebar"
        className={cn(
          'hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex',
          '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
          'hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full',
          '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
          '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
          className,
        )}
        {...props}
      />
    );
  },
);
DoubleSidebarRail.displayName = 'DoubleSidebarRail';

const DoubleSidebarInset = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'main'>
>(({ className, ...props }, ref) => {
  return (
    <main
      ref={ref}
      className={cn(
        'bg-background relative flex w-full flex-1 flex-col',
        'md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow',
        className,
      )}
      {...props}
    />
  );
});
DoubleSidebarInset.displayName = 'DoubleSidebarInset';

const DoubleSidebarInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentProps<typeof Input>
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        'bg-background focus-visible:ring-sidebar-ring h-8 w-full shadow-none focus-visible:ring-2',
        className,
      )}
      {...props}
    />
  );
});
DoubleSidebarInput.displayName = 'DoubleSidebarInput';

const DoubleSidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
});
DoubleSidebarHeader.displayName = 'DoubleSidebarHeader';

const DoubleSidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
});
DoubleSidebarFooter.displayName = 'DoubleSidebarFooter';

const DoubleSidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn('bg-sidebar-border mx-2 w-auto', className)}
      {...props}
    />
  );
});
DoubleSidebarSeparator.displayName = 'DoubleSidebarSeparator';

const DoubleSidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className,
      )}
      {...props}
    />
  );
});
DoubleSidebarContent.displayName = 'DoubleSidebarContent';

const DoubleSidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col p-2', className)}
      {...props}
    />
  );
});
DoubleSidebarGroup.displayName = 'DoubleSidebarGroup';

const DoubleSidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      ref={ref as any}
      data-sidebar="group-label"
      className={cn(
        'text-sidebar-foreground/70 ring-sidebar-ring [&>svg]:size-4 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-none transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:shrink-0',
        'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
        className,
      )}
      {...props}
    />
  );
});
DoubleSidebarGroupLabel.displayName = 'DoubleSidebarGroupLabel';

const DoubleSidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref as any}
      data-sidebar="group-action"
      className={cn(
        'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground [&>svg]:size-4 absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-none transition-transform focus-visible:ring-2 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 after:md:hidden',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
});
DoubleSidebarGroupAction.displayName = 'DoubleSidebarGroupAction';

const DoubleSidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="group-content"
    className={cn('w-full text-sm', className)}
    {...props}
  />
));
DoubleSidebarGroupContent.displayName = 'DoubleSidebarGroupContent';

const DoubleSidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu"
    className={cn('flex w-full min-w-0 flex-col gap-1', className)}
    {...props}
  />
));
DoubleSidebarMenu.displayName = 'DoubleSidebarMenu';

const DoubleSidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    data-sidebar="menu-item"
    className={cn('group/menu-item relative', className)}
    {...props}
  />
));
DoubleSidebarMenuItem.displayName = 'DoubleSidebarMenuItem';

const doubleSidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        outline:
          'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
      },
      size: {
        default: 'h-8 text-sm',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:!p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const DoubleSidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof Tooltip.Content>;
  } & VariantProps<typeof doubleSidebarMenuButtonVariants>
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = 'default',
      size = 'default',
      tooltip,
      className,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const { isMobile, state } = useDoubleSidebar();

    const button = (
      <Comp
        ref={ref as any}
        data-slot="sidebar-menu-button"
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={cn(
          doubleSidebarMenuButtonVariants({ variant, size }),
          className,
        )}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    if (typeof tooltip === 'string') {
      tooltip = {
        children: tooltip,
      };
    }

    return (
      <Tooltip>
        <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
        <Tooltip.Content
          side="right"
          align="center"
          hidden={state !== 'collapsed' || isMobile}
          {...tooltip}
        />
      </Tooltip>
    );
  },
);
DoubleSidebarMenuButton.displayName = 'DoubleSidebarMenuButton';

const DoubleSidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    showOnHover?: boolean;
  }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref as any}
      data-sidebar="menu-action"
      className={cn(
        'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-none transition-transform focus-visible:ring-2 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 after:md:hidden',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        showOnHover &&
          'peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0',
        className,
      )}
      {...props}
    />
  );
});
DoubleSidebarMenuAction.displayName = 'DoubleSidebarMenuAction';

const DoubleSidebarMenuBadge = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="menu-badge"
    className={cn(
      'min-w-5 text-sidebar-foreground pointer-events-none absolute right-1 flex h-5 select-none items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums',
      'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
      'peer-data-[size=sm]/menu-button:top-1',
      'peer-data-[size=default]/menu-button:top-1.5',
      'peer-data-[size=lg]/menu-button:top-2.5',
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    {...props}
  />
));
DoubleSidebarMenuBadge.displayName = 'DoubleSidebarMenuBadge';

const DoubleSidebarMenuSkeleton = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    showIcon?: boolean;
  }
>(({ className, showIcon = false, ...props }, ref) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={cn('flex h-8 items-center gap-2 rounded-md px-2', className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-[--skeleton-width] flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
});
DoubleSidebarMenuSkeleton.displayName = 'DoubleSidebarMenuSkeleton';

const DoubleSidebarMenuSub = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu-sub"
    className={cn(
      'border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l px-2.5 py-0.5',
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    {...props}
  />
));
DoubleSidebarMenuSub.displayName = 'DoubleSidebarMenuSub';

const DoubleSidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ ...props }, ref) => <li ref={ref} {...props} />);
DoubleSidebarMenuSubItem.displayName = 'DoubleSidebarMenuSubItem';

const DoubleSidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<'a'> & {
    asChild?: boolean;
    size?: 'sm' | 'md';
    isActive?: boolean;
  }
>(({ asChild = false, size = 'md', isActive, className, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      ref={ref as any}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:text-sidebar-accent-foreground flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:shrink-0',
        'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
        size === 'sm' && 'text-xs',
        size === 'md' && 'text-sm',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
});
DoubleSidebarMenuSubButton.displayName = 'DoubleSidebarMenuSubButton';

export default Object.assign(DoubleSidebar, {
  Content: DoubleSidebarContent,
  Footer: DoubleSidebarFooter,
  Group: DoubleSidebarGroup,
  GroupAction: DoubleSidebarGroupAction,
  GroupContent: DoubleSidebarGroupContent,
  GroupLabel: DoubleSidebarGroupLabel,
  Header: DoubleSidebarHeader,
  Input: DoubleSidebarInput,
  Inset: DoubleSidebarInset,
  Menu: DoubleSidebarMenu,
  MenuAction: DoubleSidebarMenuAction,
  MenuBadge: DoubleSidebarMenuBadge,
  MenuButton: DoubleSidebarMenuButton,
  MenuItem: DoubleSidebarMenuItem,
  MenuSkeleton: DoubleSidebarMenuSkeleton,
  MenuSub: DoubleSidebarMenuSub,
  MenuSubButton: DoubleSidebarMenuSubButton,
  MenuSubItem: DoubleSidebarMenuSubItem,
  Provider: DoubleSidebarProvider,
  Rail: DoubleSidebarRail,
  Separator: DoubleSidebarSeparator,
  Trigger: DoubleSidebarTrigger,
  useDoubleSidebar,
  useDoubleSidebarWithSide,
});
